import React from "react";
import { useCountyInsights } from "@/hooks/use-county-insights";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, TrendingUp, Info, Sparkles } from "lucide-react";

interface CountyInsightsPanelProps {
  countyName: string;
  countySlug?: string;
}

export function CountyInsightsPanel({ countyName, countySlug }: CountyInsightsPanelProps) {
  const { insights, citations, isLoading, error } = useCountyInsights(countyName, countySlug);

  if (error) return null; // Fail silently — this is enrichment, not critical

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-5">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">
          Nursing Home Costs & Care Info — {countyName}
        </h2>
        <Badge variant="secondary" className="text-xs ml-auto">AI-powered</Badge>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : insights ? (
        <div className="space-y-5">
          {/* Weekly Cost */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-primary/10">
            <TrendingUp className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">Typical Weekly Cost in {countyName}</p>
              <p className="text-xl font-bold text-primary">{insights.averageWeeklyCost}</p>
              <p className="text-xs text-muted-foreground mt-1">Source: NTPF approved rates · Updated {insights.lastUpdated}</p>
            </div>
          </div>

          {/* Fair Deal Insight */}
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground mb-1">Fair Deal in {countyName}</p>
              <p className="text-sm text-muted-foreground">{insights.fairDealInsight}</p>
            </div>
          </div>

          {/* Key Facts */}
          {insights.keyFacts?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Key Facts</p>
              <ul className="space-y-2">
                {insights.keyFacts.map((fact, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="text-primary font-bold mt-0.5">·</span>
                    {fact}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Why Choose */}
          {insights.whyChooseCounty && (
            <div>
              <p className="text-sm font-semibold text-foreground mb-1">Why {countyName}?</p>
              <p className="text-sm text-muted-foreground">{insights.whyChooseCounty}</p>
            </div>
          )}

          {/* Citations */}
          {citations.length > 0 && (
            <div className="pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground mb-1 font-medium">Sources</p>
              <div className="flex flex-wrap gap-2">
                {citations.slice(0, 4).map((citation, i) => {
                  try {
                    const domain = new URL(citation).hostname.replace('www.', '');
                    return (
                      <a
                        key={i}
                        href={citation}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {domain}
                      </a>
                    );
                  } catch {
                    return null;
                  }
                })}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
